import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Download, ArrowLeft, ShieldCheck } from "lucide-react";
import { IMG } from "@/constants/bbAssets";
import { SunMark } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginWithGoogle = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso || "";
  }
};

export const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [rows, setRows] = useState([]);
  const [getawayRows, setGetawayRows] = useState([]);
  const [tab, setTab] = useState("youth");
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) return undefined;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
    return undefined;
  }, [user]);

  const loadRows = () => {
    setLoadingRows(true);
    setError("");
    axios
      .get(`${API}/interest-list`, { withCredentials: true })
      .then((res) => setRows(res.data || []))
      .catch(() => setError("Couldn't load the list. Try refreshing."))
      .finally(() => setLoadingRows(false));
    axios
      .get(`${API}/getaway-interest`, { withCredentials: true })
      .then((res) => setGetawayRows(res.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (user) loadRows();
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setUser(null);
    navigate("/admin", { replace: true, state: {} });
  };

  const downloadCsv = async () => {
    try {
      const path = tab === "youth" ? "interest-list" : "getaway-interest";
      const file = tab === "youth" ? "beach-break-interest-list.csv" : "beach-break-getaway-interest.csv";
      const res = await axios.get(`${API}/${path}/export`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Couldn't download the CSV. Try again.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4">
        <SunMark className="w-12 h-9 text-sun animate-pulse" />
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-deep">Checking session…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5">
        <div data-testid="admin-login-card" className="bg-white border border-deep/10 rounded-2xl shadow-[0_18px_40px_-24px_rgba(11,49,68,0.4)] p-8 md:p-10 max-w-sm w-full text-center">
          <img src={IMG.logo} alt="Beach Break Sand Volleyball" className="h-14 mx-auto" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-aqua" /> Owner login
          </p>
          <h1 className="mt-2 font-display uppercase text-3xl text-deep">Interest List Admin</h1>
          <p className="mt-3 text-sm text-deep-soft leading-relaxed">
            Sign in with the Beach Break Google account to view and export interest-list signups.
          </p>
          <button
            data-testid="admin-google-signin-button"
            onClick={loginWithGoogle}
            className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-deep text-cream font-mono text-[11px] uppercase tracking-[0.18em] px-6 py-4 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
              <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4c-.2 1.2-.9 2.2-2 2.9v2.4h3.2c1.9-1.7 3-4.3 3-7.1z" />
              <path fill="currentColor" d="M12 22c2.7 0 4.9-.9 6.6-2.4l-3.2-2.4c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6C4.8 20.1 8.1 22 12 22z" />
              <path fill="currentColor" d="M6.4 14.1c-.2-.6-.3-1.3-.3-2.1s.1-1.4.3-2.1V7.3H3.1C2.4 8.7 2 10.3 2 12s.4 3.3 1.1 4.7l3.3-2.6z" />
              <path fill="currentColor" d="M12 5.8c1.5 0 2.8.5 3.8 1.5l2.9-2.9C16.9 2.9 14.7 2 12 2 8.1 2 4.8 3.9 3.1 7.3l3.3 2.6c.8-2.3 3-4.1 5.6-4.1z" />
            </svg>
            Sign in with Google
          </button>
          <button
            data-testid="admin-back-to-site"
            onClick={() => navigate("/")}
            className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-deep/60 hover:text-deep transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to the site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-deep" data-testid="admin-dashboard">
      <header className="bg-cream/95 backdrop-blur border-b border-deep/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-[76px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="Beach Break Sand Volleyball" className="h-10 md:h-12 w-auto" />
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60">
              Interest List Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.16em] text-deep/60">
              {user.email}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-deep/25 text-deep font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 rounded-full hover:bg-deep hover:text-cream transition-colors duration-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-deep/70 flex items-center gap-2">
              <span className="w-8 h-px bg-sun" /> Beach Break Volleyball
            </p>
            <h1 className="mt-2 font-display uppercase text-4xl md:text-5xl">
              Interest List
              <span className="text-sun">.</span>
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                data-testid="admin-tab-youth"
                onClick={() => setTab("youth")}
                className={`font-mono text-[10px] uppercase tracking-[0.16em] px-4 py-2 rounded-full border transition-colors duration-200 ${
                  tab === "youth" ? "bg-deep text-cream border-deep" : "border-deep/25 text-deep hover:border-deep"
                }`}
              >
                Youth Signups ({rows.length})
              </button>
              <button
                data-testid="admin-tab-getaway"
                onClick={() => setTab("getaway")}
                className={`font-mono text-[10px] uppercase tracking-[0.16em] px-4 py-2 rounded-full border transition-colors duration-200 ${
                  tab === "getaway" ? "bg-deep text-cream border-deep" : "border-deep/25 text-deep hover:border-deep"
                }`}
              >
                Getaway Signups ({getawayRows.length})
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={loadRows}
              className="inline-flex items-center gap-2 border border-deep/25 text-deep font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full hover:bg-deep hover:text-cream transition-colors duration-200"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRows ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-download-csv-button"
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 bg-deep text-cream font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
            >
              <Download className="w-3.5 h-3.5" /> Download CSV
            </button>
          </div>
        </div>

        {error && (
          <p role="alert" data-testid="admin-error" className="mt-6 text-sm font-semibold text-[#B4552A] bg-sun/15 border border-sun/40 rounded-lg px-4 py-2.5">
            {error}
          </p>
        )}

        {tab === "youth" ? (
        <div className="mt-8 bg-white border border-deep/10 rounded-2xl overflow-hidden shadow-[0_16px_40px_-28px_rgba(11,49,68,0.35)]">
          {rows.length === 0 && !loadingRows ? (
            <div data-testid="admin-empty-state" className="p-12 text-center">
              <SunMark className="w-10 h-8 text-sun/50 mx-auto" />
              <p className="mt-4 font-display uppercase text-2xl text-deep">No signups yet</p>
              <p className="mt-2 text-sm text-deep-soft">
                When athletes join the interest list, they'll show up here automatically.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="admin-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="font-mono text-[9px] uppercase tracking-[0.18em] text-deep/60 border-b border-deep/10">
                    <th className="px-4 py-3.5">Date</th>
                    <th className="px-4 py-3.5">Parent/Guardian</th>
                    <th className="px-4 py-3.5">Athlete</th>
                    <th className="px-4 py-3.5">Age</th>
                    <th className="px-4 py-3.5">Email</th>
                    <th className="px-4 py-3.5">Interested In</th>
                    <th className="px-4 py-3.5">Phone</th>
                    <th className="px-4 py-3.5">Experience</th>
                    <th className="px-4 py-3.5">Club/Team</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} data-testid={`admin-row-${i}`} className="border-b border-deep/5 last:border-0 hover:bg-tint/50">
                      <td className="px-4 py-3.5 whitespace-nowrap text-deep-soft">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3.5 font-semibold">{r.parent_name}</td>
                      <td className="px-4 py-3.5">{r.athlete_name}</td>
                      <td className="px-4 py-3.5">{r.athlete_age}</td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.email}</td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-[9px] uppercase tracking-[0.14em] bg-aqua/15 border border-aqua/40 rounded-full px-2.5 py-1">
                          {r.interested_in}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.phone || "—"}</td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.experience || "—"}</td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.club || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        ) : (
        <div className="mt-8 bg-white border border-deep/10 rounded-2xl overflow-hidden shadow-[0_16px_40px_-28px_rgba(11,49,68,0.35)]">
          {getawayRows.length === 0 && !loadingRows ? (
            <div data-testid="admin-getaway-empty" className="p-12 text-center">
              <SunMark className="w-10 h-8 text-sun/50 mx-auto" />
              <p className="mt-4 font-display uppercase text-2xl text-deep">No Getaway interest yet</p>
              <p className="mt-2 text-sm text-deep-soft">
                When adults join The Getaway list, they'll show up here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="admin-getaway-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="font-mono text-[9px] uppercase tracking-[0.18em] text-deep/60 border-b border-deep/10">
                    <th className="px-4 py-3.5">Date</th>
                    <th className="px-4 py-3.5">Name</th>
                    <th className="px-4 py-3.5">Email</th>
                    <th className="px-4 py-3.5">Phone</th>
                    <th className="px-4 py-3.5">Age</th>
                    <th className="px-4 py-3.5">Background</th>
                  </tr>
                </thead>
                <tbody>
                  {getawayRows.map((r, i) => (
                    <tr key={r.id || i} data-testid={`admin-getaway-row-${i}`} className="border-b border-deep/5 last:border-0 hover:bg-tint/50">
                      <td className="px-4 py-3.5 whitespace-nowrap text-deep-soft">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3.5 font-semibold">{r.name}</td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.email}</td>
                      <td className="px-4 py-3.5 text-deep-soft">{r.phone || "—"}</td>
                      <td className="px-4 py-3.5">{r.age_range}</td>
                      <td className="px-4 py-3.5">{r.background}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        <p className="mt-8 font-mono text-[9px] uppercase tracking-[0.2em] text-deep/40 text-center">
          Good Vibes × Volleyball — Tampa, Florida • Summer 2027
        </p>
      </main>
    </div>
  );
};

export default Admin;
